import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { ElderToastService, ElderTranslateService, RestClientPaged } from '@elderbyte/ngx-starter'
import { Observable, of } from 'rxjs'
import { filter, mergeMap } from 'rxjs/operators'
import { truthyPredicate } from '../../../../functional.util'
import { environment } from '../../../../environments/environment'
import { StorageNode } from '../nodes/storage-node'
import {
  IStorageNodeSelectionDialogOptions,
  StorageNodeSelectionDialogComponent,
} from '../nodes/storage-node-selection-dialog/storage-node-selection-dialog.component'
import {
  StorageOperation,
  StorageOperationResult,
  StorageOperationResultType,
} from './storage-operation'
import { CheckIn, Retraction, StorageOperationRequest } from './storage-operation-request'

@Injectable({
  providedIn: 'root',
})
export class StorageOperationsService extends RestClientPaged<StorageOperationRequest, string> {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly dialog = inject(MatDialog)
  private readonly toastService = inject(ElderToastService)
  private readonly translate = inject(ElderTranslateService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const http = inject(HttpClient)

    super(environment.apiPath + '/storage/operations', http, {
      idField: 'id',
    })
  }

  /***************************************************************************
   *                                                                         *
   * Check-in                                                                *
   *                                                                         *
   **************************************************************************/

  public checkIn(checkIn: CheckIn): Observable<StorageOperation | null> {
    const request = StorageOperationRequest.checkIn(checkIn)

    return this.createRequest(request).pipe(
      mergeMap((operation) => this.handleCheckInResult(operation))
    )
  }

  private handleCheckInResult(operation: StorageOperation): Observable<StorageOperation | null> {
    if (operation.result.resultType === StorageOperationResultType.INPUT_REQUIRED) {
      return this.handleStorageInputRequired(operation.request.checkIn, operation.result)
    } else {
      return of(operation)
    }
  }

  private handleStorageInputRequired(
    checkIn: CheckIn,
    result: StorageOperationResult
  ): Observable<StorageOperation | null> {
    return this.openNodeSelectionChooser(result.suitedLocationIds).pipe(
      filter(truthyPredicate),
      mergeMap((node) => {
        checkIn.targetLocation = node!.path
        return this.checkIn(checkIn)
      })
    )
  }

  private openNodeSelectionChooser(nodeIds: string[]): Observable<StorageNode | null> {
    if (nodeIds?.length > 0) {
      return this.dialog
        .open(StorageNodeSelectionDialogComponent, {
          maxWidth: '100vw',
          maxHeight: '100vh',
          width: '100vw',
          height: '100vh',
          disableClose: true,
          data: <IStorageNodeSelectionDialogOptions>{
            storageNodesIds: nodeIds,
          },
        })
        .afterClosed()
    } else {
      this.toastService.warn(this.translate.instant('shopfloor.storage.nodes.msg.all-unavailable'))
      return of(null)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Retract                                                                 *
   *                                                                         *
   **************************************************************************/

  public retract(packageIds: string[]): Observable<StorageOperation> {
    const request = StorageOperationRequest.retract(new Retraction(packageIds))
    return this.createRequest(request)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public createRequest(request: StorageOperationRequest): Observable<StorageOperation> {
    return this.http.post<StorageOperation>(this.restEndpoint, request)
  }
}
